import { useEffect, useState } from 'react';
import Loader from '@src/app-components/loader/Loader';
import NotLoggedIn from '@pages/NotLoggedIn';
import { useUserProfile } from '@hooks';
import './dashboard.scss';
import { Navigate } from '@tanstack/react-router';
import ApplicantDashboard from './views/ApplicantDashboard';
import RegulatorDashboard from './views/RegulatorDashboard';
import { ProfileRoles } from '@src/utils/enums';
import { Alert } from '@trussworks/react-uswds';
import EnvironmentVisibility, { ENVIRONMENTS } from '@components/environment-visibility/EnvironmentVisibility';
import UnderConstructionNotice from './components/UnderConstructionNotice';

const isProduction = import.meta.env.VITE_ENVIRONMENT === ENVIRONMENTS.prod;

const noticeByRole = {
  [ProfileRoles.USACERegulator]:
    'Regulator dashboard is currently in progress. Temporary applicant dashboard is shown below.',
  [ProfileRoles.HelpDesk]:
    'Help Desk dashboard is currently in progress. Temporary applicant dashboard is shown below.',
  [ProfileRoles.ExternalAgency]:
    'External Agency dashboard is currently in progress. Temporary applicant dashboard is shown below.',
  [ProfileRoles.Agent]: 'Agent dashboard is currently in progress. Temporary applicant dashboard is shown below.',
  [ProfileRoles.Developer]:
    'Developer dashboard is currently in progress. Temporary applicant dashboard is shown below.',
};

const dashboardByRole = {
  [ProfileRoles.USACERegulator]: <RegulatorDashboard />,
  [ProfileRoles.HelpDesk]: <ApplicantDashboard />,
  [ProfileRoles.ExternalAgency]: <ApplicantDashboard />,
  [ProfileRoles.Agent]: <ApplicantDashboard />,
  [ProfileRoles.Developer]: <ApplicantDashboard />,
};

// TODO: Once regulator and help desk dashboards are developed, update this component to route to the appropriate dashboard
const TemporaryDashboardNotice = ({ role }) => {
  const message = noticeByRole[role];
  if (!message) return null;

  return (
    <EnvironmentVisibility mode='hide' prod>
      <Alert type='info' headingLevel='h2' slim style={{ marginBottom: '1rem' }}>
        {message}
      </Alert>
    </EnvironmentVisibility>
  );
};

const DashboardWithNotice = ({ role }) => (
  <>
    <TemporaryDashboardNotice role={role} />
    {dashboardByRole[role] || <div>Dashboard Currently Unavailable</div>}
  </>
);

const renderDashboardForRole = (role) => {
  switch (role) {
    case ProfileRoles.Applicant:
      return <ApplicantDashboard />;

    case ProfileRoles.Agent:
      return isProduction ? <ApplicantDashboard /> : <DashboardWithNotice role={role} />;

    case ProfileRoles.USACERegulator:
      return isProduction ? <UnderConstructionNotice role={role} /> : <RegulatorDashboard />;

    case ProfileRoles.Developer:
    case ProfileRoles.HelpDesk:
    case ProfileRoles.ExternalAgency:
      return isProduction ? <UnderConstructionNotice role={role} /> : <DashboardWithNotice role={role} />;

    case ProfileRoles.Anonymous:
      return <Navigate to='/home' replace />;

    default:
      return <div>Dashboard Currently Unavailable</div>;
  }
};

const Dashboard = () => {
  const { isUserRoleValid, userRole, isLoading, isFetching } = useUserProfile();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setHasLoadedOnce(true);
    }
  }, [isLoading, isFetching]);

  const showRoleChangeLoader = hasLoadedOnce && isFetching;

  if (isUserRoleValid === false) {
    return <NotLoggedIn />;
  }

  return (
    <div className='content-container'>
      <h1>Dashboard</h1>
      {showRoleChangeLoader ? <Loader variant='inline' showLoader /> : renderDashboardForRole(userRole)}
    </div>
  );
};
export default Dashboard;
